import * as React from 'react';
import {NavigationType, useLocation, useNavigationType} from 'react-router-dom';

/**
 * Scroll offsets keyed by URL (pathname + search). Restoration only happens
 * on POP navigations (browser back/forward), so a fresh PUSH visit to a list
 * starts at the top — and clears any stale offset for that URL.
 *
 * Offsets are deliberately NOT keyed by `location.key`: some list views
 * normalize their filters into the URL on mount via `setSearchParams`, which
 * pushes a new history entry (new key) for the same URL and would orphan the
 * saved offset before it could be applied.
 *
 * The browser's native restoration only handles window scroll, which never
 * scrolls in this app's fixed-header layout, so the inner `overflow: auto`
 * container is tracked here instead.
 */
const savedPositions = new Map<string, number>();

// Bounds memory over a long session; oldest entries are dropped first.
const MAX_SAVED_POSITIONS = 50;

/**
 * @param stateKey - Extra key material for list state that is NOT reflected
 * in the URL (e.g. a filter held in component state). Without it, an offset
 * saved under one logical list state could be restored onto another that
 * happens to share the URL.
 */
export const useScrollRestoration = (stateKey?: string) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  const key =
    location.pathname + location.search + (stateKey ? `|${stateKey}` : '');
  // Flips to true once the saved offset has been applied or invalidated.
  // Until then the restore effect below retries on every commit, because
  // list content typically renders several commits after mount.
  const done = React.useRef(false);
  const lastLocationKey = React.useRef<string | null>(null);
  const lastKey = React.useRef<string | null>(null);

  // Decides, whenever the key changes, whether it has a pending restore.
  // A key change caused by a navigation (location.key changed with it)
  // restores on POP — covering both fresh mounts and filter URLs swapped in
  // place while the list stays mounted. A key change WITHOUT a navigation is
  // a state-held filter change: a new logical list that must not inherit
  // another state's offset. Deliberately depless rather than keyed on [key]:
  // the mount-time filter normalization described above pushes a new
  // location.key for the same URL, and must only update the navigation
  // marker — never cancel a pending restore.
  React.useLayoutEffect(() => {
    const navigated = location.key !== lastLocationKey.current;
    lastLocationKey.current = location.key;
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (
      navigated &&
      navigationType === NavigationType.Pop &&
      savedPositions.has(key)
    ) {
      done.current = false;
    } else {
      done.current = true;
      savedPositions.delete(key);
    }
  });

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const handleScroll = () => {
      // Any scroll — user-initiated or the restore itself — becomes the
      // position of record. A user who starts scrolling before the data
      // finishes loading also cancels the pending restore, rather than
      // being yanked to the old position later.
      done.current = true;
      savedPositions.delete(key);
      savedPositions.set(key, el.scrollTop);
      if (savedPositions.size > MAX_SAVED_POSITIONS) {
        const oldest = savedPositions.keys().next().value;
        if (oldest !== undefined) {
          savedPositions.delete(oldest);
        }
      }
    };
    el.addEventListener('scroll', handleScroll, {passive: true});
    return () => el.removeEventListener('scroll', handleScroll);
  }, [key]);

  // No dependency array: content arrives asynchronously, so retry after
  // every commit until the container is tall enough to reach the offset.
  React.useEffect(() => {
    const el = ref.current;
    if (done.current || !el) return;
    const saved = savedPositions.get(key);
    if (saved === undefined) {
      done.current = true;
      return;
    }
    if (el.scrollHeight - el.clientHeight >= saved) {
      done.current = true;
      el.scrollTop = saved;
    }
  });

  return ref;
};
